import { AxiosInstance } from 'axios';
import { get, post, patch, del } from '../hooks/useAxiosPrivate';
import {
  FieldPersonnelEntity,
  FieldPersonnelSettings,
  FieldPersonnelStatus,
  FieldPersonnelType,
} from '../state/fieldPersonnel/types';

interface GetFieldPersonnelParams {
  limit?: number;
  page?: number;
  sortColumn?: keyof FieldPersonnelEntity;
  sortDirection?: 1 | -1;
  search?: string;
  status?: FieldPersonnelStatus;
  paginate?: boolean;
}

export interface FieldPersonnelInput {
  name: string;
  type: FieldPersonnelType;
  status?: FieldPersonnelStatus;
  email?: string;
  phone?: string;
  onboardingDate?: string;
  notes?: string;
}

export type FieldPersonnelSettingsInput = Partial<
  Omit<FieldPersonnelSettings, '_id' | 'company'>
>;

const fieldPersonnelUrl = (companyId: string) =>
  `/api/v1/company/${companyId}/field-personnel`;

const FieldPersonnelApi = {
  getFieldPersonnel: async (
    axios: AxiosInstance,
    companyId: string,
    params?: GetFieldPersonnelParams
  ): Promise<{ entities: FieldPersonnelEntity[]; totalPages: number }> => {
    const response = await get(axios, fieldPersonnelUrl(companyId), params);
    return response.data;
  },
  createFieldPersonnel: async (
    axios: AxiosInstance,
    companyId: string,
    personnel: FieldPersonnelInput
  ): Promise<FieldPersonnelEntity> => {
    const response = await post(axios, fieldPersonnelUrl(companyId), personnel);
    return response.data;
  },
  updateFieldPersonnel: async (
    axios: AxiosInstance,
    companyId: string,
    id: string,
    personnel: FieldPersonnelInput
  ): Promise<FieldPersonnelEntity> => {
    const response = await patch(
      axios,
      `${fieldPersonnelUrl(companyId)}/${id}`,
      personnel
    );
    return response.data;
  },
  deleteFieldPersonnel: async (
    axios: AxiosInstance,
    companyId: string,
    id: string
  ): Promise<void> => {
    await del(axios, `${fieldPersonnelUrl(companyId)}/${id}`);
  },
  getSettings: async (
    axios: AxiosInstance,
    companyId: string
  ): Promise<FieldPersonnelSettings> => {
    const response = await get(
      axios,
      `${fieldPersonnelUrl(companyId)}/settings`
    );
    return response.data;
  },
  updateSettings: async (
    axios: AxiosInstance,
    companyId: string,
    settings: FieldPersonnelSettingsInput
  ): Promise<FieldPersonnelSettings> => {
    const response = await patch(
      axios,
      `${fieldPersonnelUrl(companyId)}/settings`,
      settings
    );
    return response.data;
  },
};

export default FieldPersonnelApi;
